import { useCallback, useEffect, useMemo, useRef } from "react";
import { actualizarCuotas } from "../../utils/actualizarCuotas";
import {
  debeEliminarClientePagadoEnFecha,
  procesarClientesPagadosDeRuta,
} from "../../utils/clientesPagados";
import type {
  AnyObject,
  UseActualizarCuotasOnMountParams,
  UseCrearRutaHandlerParams,
  UseEliminarClientesPagadosParams,
  UseHomePagePaymentAlertsParams,
} from "./types";

export {
  debeEliminarClientePagado,
  debeEliminarPorFecha,
  obtenerUltimaFechaPago,
} from "../../utils/clientesPagados";

export const calcularRutasDisponibles = (
  userData: AnyObject | null,
  rutasData: AnyObject[] | null,
): number => {
  return Number(userData?.cantidadRutas ?? 0) - (rutasData?.length ?? 0);
};

export const obtenerPosicionArroba = (email?: string): number => {
  return email?.indexOf("@") ?? -1;
};

export const calcularSumaPagosDiarios = (infoClientes: AnyObject[] | null): number => {
  if (!infoClientes?.length) {
    return 0;
  }

  return infoClientes.reduce((suma, cliente) => suma + Number(cliente.pagoDiario ?? 0), 0);
};

const normalizarFecha = (value: AnyObject | Date | string | null | undefined): Date | null => {
  const valueAsObject = typeof value === "object" && value !== null ? value : null;

  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  if (valueAsObject && "toDate" in valueAsObject && typeof valueAsObject.toDate === "function") {
    return valueAsObject.toDate();
  }

  if (
    valueAsObject &&
    "toMillis" in valueAsObject &&
    typeof valueAsObject.toMillis === "function"
  ) {
    return new Date(valueAsObject.toMillis());
  }

  if (typeof value !== "string" && typeof value !== "number") {
    return null;
  }

  const fecha = new Date(value);

  return Number.isNaN(fecha.getTime()) ? null : fecha;
};

export const debeMostrarAvisoPago = (
  proximoPago: any,
  formatDate2: (fecha: any) => string,
): { mostrarAviso: boolean; bloquearCuenta: boolean } => {
  const fechaProximoPago = normalizarFecha(proximoPago);

  if (!fechaProximoPago) {
    return { mostrarAviso: false, bloquearCuenta: false };
  }

  const fechaHoy = new Date();
  const proximoPagoFormateado = formatDate2(fechaProximoPago);
  const diferenciaEnMillis = fechaHoy.getTime() - fechaProximoPago.getTime();
  const diferenciaEnDias = Math.floor(diferenciaEnMillis / (1000 * 60 * 60 * 24));

  if (
    proximoPagoFormateado === formatDate2(fechaHoy) ||
    (diferenciaEnDias > 0 && diferenciaEnDias <= 2)
  ) {
    return { mostrarAviso: true, bloquearCuenta: false };
  }

  if (diferenciaEnDias >= 3) {
    return { mostrarAviso: false, bloquearCuenta: true };
  }

  return { mostrarAviso: false, bloquearCuenta: false };
};

export const useHomePageMetrics = (
  userData: AnyObject | null,
  rutasData: AnyObject[] | null,
  infoClientes: AnyObject[] | null,
) => {
  return useMemo(
    () => ({
      rutasDisponibles: calcularRutasDisponibles(userData, rutasData),
      posicionArroba: obtenerPosicionArroba(userData?.email),
      sumaPagosDiarios: calcularSumaPagosDiarios(infoClientes),
    }),
    [infoClientes, rutasData, userData],
  );
};

export const useActualizarCuotasOnMount = ({
  primerRender,
  infoClientes,
  setInfoClientes,
  setPrimerRender,
  usuarioRuta,
}: UseActualizarCuotasOnMountParams) => {
  useEffect(() => {
    if (!primerRender || !usuarioRuta || infoClientes === null) {
      return;
    }

    let isCancelled = false;

    const syncCuotasOnMount = async (): Promise<void> => {
      if (!infoClientes.length) {
        if (!isCancelled) {
          setPrimerRender(false);
        }

        return;
      }

      const clientesActualizados = await actualizarCuotas({
        infoClientes,
        usuarioRuta,
      });

      if (isCancelled) {
        return;
      }

      if (clientesActualizados && clientesActualizados !== infoClientes) {
        setInfoClientes(clientesActualizados);
      }

      setPrimerRender(false);
    };

    void syncCuotasOnMount();

    return () => {
      isCancelled = true;
    };
  }, [infoClientes, primerRender, setInfoClientes, setPrimerRender, usuarioRuta]);
};

export const useHomePagePaymentAlerts = ({
  userData,
  usuarioRuta,
  formatDate2,
  setRutasData,
  setInfoClientes,
  setIsAvisoPago,
  setIsLockedAcount,
}: UseHomePagePaymentAlertsParams) => {
  const aplicarEstadoPago = useCallback(
    (proximoPago: any, onBloquear: () => void) => {
      const { mostrarAviso, bloquearCuenta } = debeMostrarAvisoPago(proximoPago, formatDate2);

      if (mostrarAviso) {
        setIsAvisoPago(true);
        return;
      }

      if (bloquearCuenta) {
        setIsLockedAcount(true);
        onBloquear();
      }
    },
    [formatDate2, setIsAvisoPago, setIsLockedAcount],
  );

  useEffect(() => {
    if (!userData) {
      return;
    }

    aplicarEstadoPago(userData.proximoPago, () => {
      setRutasData(null);
    });
  }, [aplicarEstadoPago, setRutasData, userData]);

  useEffect(() => {
    if (!usuarioRuta) {
      return;
    }

    aplicarEstadoPago(usuarioRuta.proximoPago, () => {
      setInfoClientes(null);
    });
  }, [aplicarEstadoPago, setInfoClientes, usuarioRuta]);
};

export const useEliminarClientesPagados = ({
  infoClientes,
  setInfoClientes,
  usuarioRuta,
}: UseEliminarClientesPagadosParams) => {
  const deletingClientesRef = useRef<Set<string>>(new Set());
  const adminUid = usuarioRuta?.adminUid;
  const rutaUid = usuarioRuta?.uid;

  const eliminarClientesPagados = useCallback(async (): Promise<void> => {
    if (!infoClientes?.length || !adminUid || !rutaUid) {
      return;
    }

    const fechaHoy = new Date();
    const clientesAEliminar = infoClientes.filter((cliente) => {
      if (!cliente.uid || deletingClientesRef.current.has(cliente.uid)) {
        return false;
      }

      return debeEliminarClientePagadoEnFecha(cliente, fechaHoy);
    });

    if (!clientesAEliminar.length) {
      return;
    }

    const clienteIds = clientesAEliminar.map((cliente) => cliente.uid as string);

    clienteIds.forEach((clienteUid) => {
      deletingClientesRef.current.add(clienteUid);
    });

    const { clientesEliminados } = await procesarClientesPagadosDeRuta({
      adminUid,
      clientes: clientesAEliminar,
      fechaReferencia: fechaHoy,
      rutaUid,
    }).finally(() => {
      clienteIds.forEach((clienteUid) => {
        deletingClientesRef.current.delete(clienteUid);
      });
    });

    if (!clientesEliminados.length) {
      return;
    }

    const clientesEliminadosSet = new Set(clientesEliminados);

    setInfoClientes(
      (previousClientes) =>
        previousClientes?.filter((cliente) =>
          cliente.uid ? !clientesEliminadosSet.has(cliente.uid) : true,
        ) ?? previousClientes,
    );
  }, [adminUid, infoClientes, rutaUid, setInfoClientes]);

  useEffect(() => {
    void eliminarClientesPagados();
  }, [eliminarClientesPagados]);
};

export const useCrearRutaHandler = ({
  rutasDisponibles,
  setIsModalRutasNoDisponibles,
  setIsModalCreateRuta,
}: UseCrearRutaHandlerParams) => {
  return useCallback(() => {
    if (rutasDisponibles === 0) {
      setIsModalRutasNoDisponibles(true);
    } else {
      setIsModalCreateRuta(true);
    }
  }, [rutasDisponibles, setIsModalCreateRuta, setIsModalRutasNoDisponibles]);
};
